import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// Helper functions for sanitizing API responses
const sanitizeData = (obj) => {
  if (!obj) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeData(item));
  }
  const result = {};
  for (const key in obj) {
    if (typeof obj[key] === 'number') {
      if (!isFinite(obj[key])) {
        result[key] = 0;
      } else {
        result[key] = obj[key];
      }
    } else if (typeof obj[key] === 'object') {
      result[key] = sanitizeData(obj[key]);
    } else {
      result[key] = obj[key];
    }
  }
  return result;
};

const cleanJsonString = (jsonStr) => {
  if (typeof jsonStr !== 'string') return jsonStr;
  return jsonStr
    .replace(/:\s*NaN\s*,/g, ': 0,')
    .replace(/:\s*Infinity\s*,/g, ': 0,')
    .replace(/:\s*-Infinity\s*,/g, ': 0,')
    .replace(/:\s*NaN\s*\}/g, ': 0}')
    .replace(/:\s*Infinity\s*\}/g, ': 0}')
    .replace(/:\s*-Infinity\s*\}/g, ': 0}');
};

const safeJsonParse = (jsonStr, defaultValue = {}) => {
  if (typeof jsonStr !== 'string') return jsonStr;
  try {
    const cleanedStr = cleanJsonString(jsonStr);
    const parsed = JSON.parse(cleanedStr);
    return sanitizeData(parsed);
  } catch (error) {
    console.error('Failed to parse JSON string:', error);
    return defaultValue;
  }
};

const getToken = () => {
  return localStorage.getItem('token');
};

const rrApiClient = axios.create({
  baseURL: config.api.rrServiceUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 600000 // 600 seconds, adjust as needed
});

rrApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('RR API Client Request interceptor error:', error);
    return Promise.reject(error);
  }
);

rrApiClient.interceptors.response.use(
  (response) => {
    if (response.data instanceof Blob) {
      return response;
    }
    if (response.data && typeof response.data === 'object') {
      response.data = sanitizeData(response.data);
    }
    return response;
  },
  (error) => {
    console.error('RR API Client Response Error:', error.response?.status, error.config?.url);
    return Promise.reject(error);
  }
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(rrApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(rrApiClient);

const rrService = {
  /**
   * Get rebalancer constants like cash-like symbols.
   * @returns {Promise<{cash_like_symbols: string[], cash_symbols_pattern: string}>}
   */
  getConstants: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/constants');
      return response.data;
    } catch (error) {
      console.error('Error fetching constants:', error);
      // Return default values if API fails
      return {
        cash_like_symbols: ['FCASH', 'SPAXX', 'SWVXX', 'VMFXX', 'FDRXX', 'SPRXX', 'FZFXX', 'SNSXX', 'SNVXX'],
        cash_symbols_pattern: 'CASH|Cash|cash'
      };
    }
  },

  getRebalancingGroups: async () => {
    try {
      let url = '/rebalancer-reports/rebalancing-groups';

      const response = await rrApiClient.get(url);
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = safeJsonParse(data, { groups: [] });
        } catch (parseError) {
          console.error('Failed to parse rebalancing groups response as JSON:', parseError);
          return { groups: [] };
        }
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching rebalancing groups:', error);
      return { groups: [] };
    }
  },

  getGroupHighLevelInfo: async (groupName) => {
    try {
      let url = `/rebalancer-reports/group/${encodeURIComponent(groupName)}/high-level-info`;

      const response = await rrApiClient.get(url);
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = safeJsonParse(data, { data: [] });
        } catch (parseError) {
          console.error('Failed to parse high level info response as JSON:', parseError);
          return { data: [] };
        }
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching group high level info:', error);
      return { data: [] };
    }
  },

  getGroupBySecuritiesReport: async (groupName) => {
    try {
      let url = `/rebalancer-reports/group/${encodeURIComponent(groupName)}/by-securities`;

      const response = await rrApiClient.get(url);
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = safeJsonParse(data, { data: [] });
        } catch (parseError) {
          console.error('Failed to parse securities report response as JSON:', parseError);
          return { data: [] };
        }
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching group by securities report:', error);
      return { data: [] };
    }
  },

  getGroupByAccountReport: async (groupName) => {
    try {
      let url = `/rebalancer-reports/group/${encodeURIComponent(groupName)}/by-account`;

      const response = await rrApiClient.get(url);
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = safeJsonParse(data, { data: [] });
        } catch (parseError) {
          console.error('Failed to parse account report response as JSON:', parseError);
          return { data: [] };
        }
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching group by account report:', error);
      return { data: [] };
    }
  },

  getGroupCashActionsReport: async (groupName) => {
    try {
      let url = `/rebalancer-reports/group/${encodeURIComponent(groupName)}/cash-actions`;

      const response = await rrApiClient.get(url);
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = safeJsonParse(data, { data: [] });
        } catch (parseError) {
          console.error('Failed to parse cash actions response as JSON:', parseError);
          return { data: [] };
        }
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching group cash actions report:', error);
      return { data: [] };
    }
  },

  getGroupTradeRestrictionsReport: async (groupName) => {
    try {
      let url = `/rebalancer-reports/group/${encodeURIComponent(groupName)}/trade-restrictions`;

      const response = await rrApiClient.get(url);
      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = safeJsonParse(data, { data: [] });
        } catch (parseError) {
          console.error('Failed to parse trade restrictions response as JSON:', parseError);
          return { data: [] };
        }
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching group trade restrictions report:', error);
      return { data: [] };
    }
  },

  calculateCombinedResultingTarget: async (groupName, securityLevelModel, trades = [], options = {}) => {
    try {
      const requestBody = {
        group_name: groupName,
        security_models: [
          {
            security_level_model: securityLevelModel,
            trades: trades
          }
        ]
      };
      const response = await rrApiClient.post('/rebalancer-reports/calculate-combined-resulting-target', requestBody);
      if (response.data && response.data.results && response.data.results.length > 0) {
        const firstResult = response.data.results[0];
        return {
          combined_resulting_target: firstResult.combined_resulting_target,
          group_name: response.data.group_name,
          security_level_model: firstResult.security_level_model,
          trades_count: firstResult.trades_count,
          trades: firstResult.trades,
          calculation_method: firstResult.calculation_method
        };
      }
      return response.data;
    } catch (error) {
      console.error('Calculate combined resulting target error:', error);
      throw error;
    }
  },

  calculateCombinedResultingTargetBulk: async (groupName, securityModels = [], options = {}) => {
    try {
      const requestData = {
        group_name: groupName,
        security_models: securityModels
      };
      const response = await rrApiClient.post('/rebalancer-reports/calculate-combined-resulting-target', requestData);
      return response.data;
    } catch (error) {
      console.error('Calculate combined resulting target bulk error:', error);
      throw error;
    }
  },

  calculateAccountCashLevels: async (groupName, accountNumber, trades = [], options = {}) => {
    try {
      const params = new URLSearchParams();
      if (groupName) params.append('group_name', groupName);
      if (accountNumber) params.append('account_number', accountNumber);
      if (trades && Array.isArray(trades)) params.append('trades', JSON.stringify(trades));
      const response = await rrApiClient.get(`/rebalancer-reports/calculate-account-cash-levels?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Calculate account cash levels error:', error);
      throw error;
    }
  },

  getSymbolsForSecurityLevelModel: async (modelName, options = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('security_level_model', modelName);
      if (options.account_number) params.append('account_number', options.account_number);
      if (options.accountNumber) params.append('account_number', options.accountNumber);
      const response = await rrApiClient.get(`/rebalancer-reports/symbols?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get symbols for security level model error:', error);
      throw error;
    }
  },

  getBulkSymbolsForSecurityLevelModels: async (requests, options = {}) => {
    try {
      const requestData = {
        requests: requests
      };
      const response = await rrApiClient.post('/rebalancer-reports/bulk-symbols', requestData);
      return response.data;
    } catch (error) {
      console.error('Get bulk symbols for security level models error:', error);
      throw error;
    }
  },

  getAccountHoldings: async (accountNumber, symbol = null, options = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('account_number', accountNumber);
      if (symbol) params.append('symbol', symbol);
      const response = await rrApiClient.get(`/rebalancer-reports/account-holdings?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get account holdings error:', error);
      throw error;
    }
  },

  getAccountModelWeights: async (accountNumber) => {
    try {
      const response = await rrApiClient.get(
        `/rebalancer-reports/account-model-weights?account_number=${encodeURIComponent(accountNumber)}`
      );
      return response.data;
    } catch (error) {
      console.error(`Get account model weights error for ${accountNumber}:`, error);
      throw error;
    }
  },

  generateOrderForm: async (group, accountName, accountNumber, custodian, trades) => {
    try {
      const orderData = {
        group_name: group,
        account_name: accountName,
        account_number: accountNumber,
        custodian: custodian,
        trades: trades
      };
      const response = await rrApiClient.post('/rebalancer-reports/generate-order-form', orderData, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Error generating order form:', error);
      throw error;
    }
  },

  rebalancePortfolio: async (groupName, config = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('group_name', groupName);
      if (config.deviation_threshold !== undefined) {
        params.append('deviation_threshold', config.deviation_threshold);
      }
      if (config.min_trade_value !== undefined) {
        params.append('min_trade_value', config.min_trade_value);
      }
      if (config.tax_efficiency_weight !== undefined) {
        params.append('tax_efficiency_weight', config.tax_efficiency_weight);
      }
      if (config.cash_buffer_percentage !== undefined) {
        params.append('cash_buffer_percentage', config.cash_buffer_percentage);
      }
      if (config.rebalancer_type !== undefined) {
        params.append('rebalancer_type', config.rebalancer_type);
      }
      const response = await rrApiClient.get(`/rebalancer-reports/rebalance-portfolio?${params.toString()}`);
      return sanitizeData(response.data);
    } catch (error) {
      console.error('Portfolio rebalance error:', error);
      throw error;
    }
  },

  getSymbolsForSecurityLevelModelsForAccounts: async (accountNumbers, options = {}) => {
    try {
      const requestData = {
        account_numbers: accountNumbers
      };
      const response = await rrApiClient.post('/rebalancer-reports/symbols-for-models-accounts', requestData);
      return response.data;
    } catch (error) {
      console.error('Get symbols for security level models for accounts error:', error);
      throw error;
    }
  },

  deleteFile: async (filename) => {
    try {
      const response = await rrApiClient.delete(`/rebalancer-reports/delete-file?filename=${encodeURIComponent(filename)}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  },

  // ============================================================================
  // Daily Rebalancer Dashboard Methods
  // ============================================================================

  /**
   * Get the summary of the daily raise cash run from S3.
   * @returns {Promise<Object>} Summary data from the latest daily raise cash run.
   */
  getDailyRaiseCashSummary: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/dailies/raise-cash/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching daily raise cash summary:', error);
      throw error;
    }
  },

  /**
   * Get detailed trades for a specific client from the daily raise cash run.
   * @param {string} groupName - The client/group name.
   * @returns {Promise<Object>} Detailed trade data for the specified client.
   */
  getDailyRaiseCashClientDetail: async (groupName) => {
    try {
      const response = await rrApiClient.get(`/rebalancer-reports/dailies/raise-cash/client/${encodeURIComponent(groupName)}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching daily raise cash detail for ${groupName}:`, error);
      throw error;
    }
  },

  /**
   * Get the summary of the daily invest cash run from S3.
   * @returns {Promise<Object>} Summary data from the latest daily invest cash run.
   */
  getDailyInvestCashSummary: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/dailies/invest-cash/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching daily invest cash summary:', error);
      throw error;
    }
  },

  /**
   * Get detailed trades for a specific client from the daily invest cash run.
   * @param {string} groupName - The client/group name.
   * @returns {Promise<Object>} Detailed trade data for the specified client.
   */
  getDailyInvestCashClientDetail: async (groupName) => {
    try {
      const response = await rrApiClient.get(`/rebalancer-reports/dailies/invest-cash/client/${encodeURIComponent(groupName)}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching daily invest cash detail for ${groupName}:`, error);
      throw error;
    }
  },

  /**
   * Re-run the rebalancer for a group in real time, allowing invest-cash
   * buys up to MAXIMUM target weights (sleeves and models). Used when all
   * underweight positions are blocked by buy restrictions.
   * Runs synchronously - the response contains the full rebalance result.
   * @param {string} groupName - The client/group name.
   * @returns {Promise<Object>} Full rebalance result (invest_cash_result, invest_cash_margin_result, ...).
   */
  runBuyToMaxForGroup: async (groupName) => {
    try {
      const response = await rrApiClient.post(
        `/rebalancer-reports/dailies/invest-cash/buy-to-max/${encodeURIComponent(groupName)}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error running buy-to-max rebalance for ${groupName}:`, error);
      throw error;
    }
  },

  /**
   * Get the summary of the daily mixed (raise+invest) run from S3.
   * @returns {Promise<Object>} Summary data from the latest daily mixed run.
   */
  getDailyMixedSummary: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/dailies/mixed/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching daily mixed summary:', error);
      throw error;
    }
  },

  /**
   * Get detailed trades for a specific client from the daily mixed run.
   * @param {string} groupName - The client/group name.
   * @returns {Promise<Object>} Detailed trade data for the specified client (includes both raise and invest).
   */
  getDailyMixedClientDetail: async (groupName) => {
    try {
      const response = await rrApiClient.get(`/rebalancer-reports/dailies/mixed/client/${encodeURIComponent(groupName)}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching daily mixed detail for ${groupName}:`, error);
      throw error;
    }
  },

  /**
   * Get the summary of the daily no-action groups from S3.
   * @returns {Promise<Object>} Summary data from the latest daily no-action run.
   */
  getDailyNoActionSummary: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/dailies/no-action/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching daily no-action summary:', error);
      throw error;
    }
  },

  /**
   * Get a live account-state snapshot for a no-action group.
   * Returns an invest_cash-shaped result with empty trades so the
   * dashboard can display allocations and allow manual trade entry.
   * @param {string} groupName - The client/group name.
   * @returns {Promise<Object>} Account allocation data for the specified client.
   */
  getDailyNoActionClientDetail: async (groupName) => {
    try {
      const response = await rrApiClient.get(`/rebalancer-reports/dailies/no-action/client/${encodeURIComponent(groupName)}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching no-action detail for ${groupName}:`, error);
      throw error;
    }
  },

  /**
   * Get historical rebalancer data for trend graphs.
   * @param {number} days - Number of days of history to fetch (default 30).
   * @returns {Promise<Object>} Historical data with invest_cash and raise_cash trends.
   */
  getRebalancerHistory: async (days = 30) => {
    try {
      const response = await rrApiClient.get(`/rebalancer-reports/history?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching rebalancer history:', error);
      throw error;
    }
  },

  /**
   * Get detailed cash reserve information for a specific account.
   * Returns all cash reserve entries including category, notes, dates, and thresholds.
   * @param {string} accountNumber - The account number to get details for.
   * @param {boolean} useCache - Whether to use cached data (default true).
   * @returns {Promise<Object>} Detailed cash reserve data for the account.
   */
  getAccountCashReserveDetails: async (accountNumber, useCache = true) => {
    try {
      const response = await rrApiClient.get(
        `/rebalancer-reports/account-cash-reserve-details?account_number=${encodeURIComponent(accountNumber)}&use_cache=${useCache}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching cash reserve details for ${accountNumber}:`, error);
      throw error;
    }
  },

  /**
   * Get comprehensive account details including cash reserves and trade restrictions.
   * 
   * @param {string} accountNumber - The account number to get details for.
   * @param {boolean} useCache - Whether to use cached data (default true).
   * @returns {Promise<Object>} Full account details including reserves and restrictions.
   */
  getAccountDetails: async (accountNumber, useCache = true) => {
    try {
      const response = await rrApiClient.get(
        `/rebalancer-reports/account-details?account_number=${encodeURIComponent(accountNumber)}&use_cache=${useCache}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching account details for ${accountNumber}:`, error);
      throw error;
    }
  },

  // ============================================================================
  // Queued Trades Methods
  // ============================================================================

  /**
   * Queue trades for a group to be processed later.
   * @param {string} groupName - The group/account family name.
   * @param {string} category - Category: 'raiseCash', 'investCash', or 'mixed'.
   * @param {Array} trades - Array of trade objects.
   * @param {Array} accountAllocations - Optional account allocation data.
   * @param {Array} securityAllocations - Optional security allocation data.
   * @returns {Promise<Object>} Result of the queue operation.
   */
  queueTrades: async (groupName, category, trades, accountAllocations = null, securityAllocations = null) => {
    try {
      const response = await rrApiClient.post('/rebalancer-reports/queued-trades', {
        group_name: groupName,
        category: category,
        trades: trades,
        account_allocations: accountAllocations,
        security_allocations: securityAllocations
      });
      return response.data;
    } catch (error) {
      console.error('Error queuing trades:', error);
      throw error;
    }
  },

  /**
   * Get queued trades for a specific group.
   * @param {string} groupName - The group/account family name.
   * @param {string} date - Optional date in YYYY-MM-DD format. Defaults to today.
   * @returns {Promise<Object>} Queued trade data or null if not found.
   */
  getQueuedTrades: async (groupName, date = null) => {
    try {
      let url = `/rebalancer-reports/queued-trades/${encodeURIComponent(groupName)}`;
      if (date) {
        url += `?date=${encodeURIComponent(date)}`;
      }
      const response = await rrApiClient.get(url);
      return response.data;
    } catch (error) {
      console.error(`Error getting queued trades for ${groupName}:`, error);
      throw error;
    }
  },

  /**
   * Get all queued trades for a specific date.
   * @param {string} date - Optional date in YYYY-MM-DD format. Defaults to today.
   * @returns {Promise<Object>} Map of group_name -> queued trade data.
   */
  getAllQueuedTrades: async (date = null) => {
    try {
      let url = '/rebalancer-reports/queued-trades';
      if (date) {
        url += `?date=${encodeURIComponent(date)}`;
      }
      const response = await rrApiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error getting all queued trades:', error);
      throw error;
    }
  },

  /**
   * Delete queued trades for a specific group.
   * @param {string} groupName - The group/account family name.
   * @param {string} date - Optional date in YYYY-MM-DD format. Defaults to today.
   * @returns {Promise<Object>} Result of the delete operation.
   */
  deleteQueuedTrades: async (groupName, date = null) => {
    try {
      let url = `/rebalancer-reports/queued-trades/${encodeURIComponent(groupName)}`;
      if (date) {
        url += `?date=${encodeURIComponent(date)}`;
      }
      const response = await rrApiClient.delete(url);
      return response.data;
    } catch (error) {
      console.error(`Error deleting queued trades for ${groupName}:`, error);
      throw error;
    }
  },

  /**
   * Get a summary of queued trades, including counts by custodian.
   * @param {string} date - Optional date in YYYY-MM-DD format. Defaults to today.
   * @returns {Promise<Object>} Summary with custodian counts.
   */
  getQueuedTradesSummary: async (date = null) => {
    try {
      let url = '/rebalancer-reports/queued-trades-summary';
      if (date) {
        url += `?date=${encodeURIComponent(date)}`;
      }
      const response = await rrApiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error getting queued trades summary:', error);
      throw error;
    }
  },

  /**
   * Generate bulk order files from all queued trades.
   * Downloads a ZIP file containing order files for each custodian (Schwab/Fidelity).
   * @param {string} date - Optional date in YYYY-MM-DD format. Defaults to today.
   * @returns {Promise<Blob>} ZIP file blob.
   */
  generateBulkOrders: async (date = null) => {
    try {
      let url = '/rebalancer-reports/generate-bulk-orders';
      if (date) {
        url += `?date=${encodeURIComponent(date)}`;
      }
      const response = await rrApiClient.post(url, null, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error generating bulk orders:', error);
      throw error;
    }
  },

  /**
   * Generate order files for selected groups only.
   * Consolidates trades into 1 file per custodian (Schwab/Fidelity).
   * @param {string[]} groupNames - Array of group names to generate orders for.
   * @param {string} date - Optional date in YYYY-MM-DD format. Defaults to today.
   * @returns {Promise<{blob: Blob, filename: string, contentType: string}>} File data.
   */
  /**
   * Refresh real-time prices and rebalance a single group.
   * @param {string} groupName - The account family / group name
   * @param {object} [options] - Optional settings
   * @param {boolean} [options.buyToMax] - Allow invest-cash buys up to maximum target weights
   * @returns {Promise<object>} Rebalance result with real-time prices
   */
  refreshPricesForGroup: async (groupName, options = {}) => {
    try {
      const response = await rrApiClient.post(
        `/rebalancer-reports/refresh-prices/group/${encodeURIComponent(groupName)}`,
        null,
        { params: options.buyToMax ? { buy_to_max: true } : {} }
      );
      let data = response.data;
      if (typeof data === 'string') {
        data = safeJsonParse(data, {});
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error refreshing prices for group:', error);
      throw error;
    }
  },

  /**
   * Start a background price refresh + rebalance for all groups.
   * Returns immediately with a job_id for polling.
   * @returns {Promise<{job_id: string, status: string, message: string}>}
   */
  refreshPricesForAll: async () => {
    try {
      const response = await rrApiClient.post('/rebalancer-reports/refresh-prices/all');
      let data = response.data;
      if (typeof data === 'string') {
        data = safeJsonParse(data, {});
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error starting price refresh for all:', error);
      throw error;
    }
  },

  /**
   * Poll the status of a background price-refresh job.
   * @param {string} jobId - The job_id returned by refreshPricesForAll
   * @returns {Promise<object>} Job status with progress, symbols, etc.
   */
  pollRefreshPricesStatus: async (jobId) => {
    try {
      const response = await rrApiClient.get(
        `/rebalancer-reports/refresh-prices/status/${encodeURIComponent(jobId)}`
      );
      let data = response.data;
      if (typeof data === 'string') {
        data = safeJsonParse(data, {});
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error polling refresh prices status:', error);
      throw error;
    }
  },

  /**
   * Get cached real-time prices.
   * @returns {Promise<object>} Cached price data
   */
  getCachedPrices: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/refresh-prices/cached');
      let data = response.data;
      if (typeof data === 'string') {
        data = safeJsonParse(data, {});
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching cached prices:', error);
      throw error;
    }
  },

  /**
   * Check whether a price-refresh job is currently running.
   * @returns {Promise<{active: boolean, job_id?: string, status?: string, progress?: string}>}
   */
  checkActiveRefreshJob: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/refresh-prices/active');
      let data = response.data;
      if (typeof data === 'string') {
        data = safeJsonParse(data, { active: false });
      } else if (data) {
        data = sanitizeData(data);
      }
      return data;
    } catch (error) {
      console.error('Error checking active refresh job:', error);
      return { active: false };
    }
  },

  generateSelectedOrders: async (groupNames, date = null) => {
    try {
      const response = await rrApiClient.post('/rebalancer-reports/generate-selected-orders', {
        group_names: groupNames,
        date: date
      }, {
        responseType: 'blob'
      });
      
      const contentType = response.headers['content-type'] || 'application/octet-stream';

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = null;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // If we couldn't read Content-Disposition (blocked by CORS expose_headers),
      // derive a sensible filename from the content-type.
      if (!filename) {
        if (contentType.includes('zip')) {
          const today = new Date().toISOString().slice(0, 10);
          filename = `Order_Files_${today}.zip`;
        } else {
          filename = 'orders.txt';
        }
      }
      
      return {
        blob: response.data,
        filename: filename,
        contentType: contentType
      };
    } catch (error) {
      console.error('Error generating selected orders:', error);
      throw error;
    }
  },

  // ====================================================================
  // Directed Trade
  // ====================================================================

  /**
   * Get the universe of tradeable symbols (every symbol held or present
   * in any model) for the Directed Trade symbol dropdown.
   * @returns {Promise<{symbols: string[], count: number}>}
   */
  getDirectedTradeSymbols: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/directed-trade/symbols');
      return response.data;
    } catch (error) {
      console.error('Error fetching directed trade symbols:', error);
      throw error;
    }
  },

  /**
   * Get the list of Tamarac bulk report templates available for
   * manual directed-trade refresh.
   * @returns {Promise<{report_names: string[], default_report_names: string[], count: number}>}
   */
  getDirectedTradeBulkReportOptions: async () => {
    try {
      const response = await rrApiClient.get('/rebalancer-reports/directed-trade/bulk-reports/options');
      return response.data;
    } catch (error) {
      console.error('Error fetching directed trade bulk report options:', error);
      throw error;
    }
  },

  /**
   * Start a manual Tamarac bulk report refresh for directed trading,
   * then poll execution status until terminal.
   *
   * @param {string[]} reportNames - Selected Tamarac report template names.
   * @param {Object} [options] - Optional overrides (environment, wait_time_seconds, max_status_checks).
   * @param {(snapshot: Object) => void} [onProgress] - Called with each status snapshot.
   * @returns {Promise<Object>} Final execution status snapshot.
   */
  startDirectedTradeBulkReportFetch: async (reportNames, options = {}, onProgress) => {
    const names = Array.isArray(reportNames)
      ? reportNames.map((name) => `${name || ''}`.trim()).filter(Boolean)
      : [];

    if (!names.length) {
      throw new Error('Select at least one Tamarac report to fetch.');
    }

    try {
      const startResp = await rrApiClient.post(
        '/rebalancer-reports/directed-trade/bulk-reports/fetch',
        {
          report_names: names,
          ...options,
        }
      );
      const executionName = startResp.data?.execution_name;
      if (!executionName) {
        return startResp.data;
      }

      const pollIntervalMs = 4000;
      const maxAttempts = 450; // ~30 minutes safety cap
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));

        let snapshot;
        try {
          // eslint-disable-next-line no-await-in-loop
          const statusResp = await rrApiClient.get(
            `/rebalancer-reports/directed-trade/bulk-reports/fetch/status/${executionName}`
          );
          snapshot = sanitizeData(statusResp.data);
        } catch (pollError) {
          console.warn('Directed trade report-refresh poll failed, retrying:', pollError?.message);
          // eslint-disable-next-line no-continue
          continue;
        }

        if (onProgress && snapshot) onProgress(snapshot);

        const status = `${snapshot?.status || ''}`.toUpperCase();
        if (status === 'SUCCEEDED') return snapshot;
        if (status === 'FAILED' || status === 'TIMED_OUT' || status === 'ABORTED') {
          throw new Error(snapshot?.error || `Report refresh ${status.toLowerCase()}.`);
        }
      }

      throw new Error('Report refresh timed out while polling for status.');
    } catch (error) {
      console.error('Error running directed trade report refresh:', error);
      throw error;
    }
  },

  /**
   * Run a directed buy/sell of a single symbol firm-wide.
   *
   * The firm-wide run takes minutes, so it executes asynchronously: this
   * starts the job, then polls for status, invoking ``onProgress`` with
   * each partial snapshot so the caller can render account families as
   * they finish. Resolves with the final completed result.
   *
   * @param {string} symbol - The ticker symbol to trade.
   * @param {string} action - 'buy' or 'sell'.
   * @param {string} mode - BUY: max_cash|to_max|to_target|to_min, SELL: sell_out|to_target|to_min|to_max.
  * @param {string} [buyCashPolicy] - BUY-only cash spending policy.
  * @param {(snapshot: Object) => void} [onProgress] - Called with each partial snapshot.
   * @returns {Promise<Object>} Final firm-wide summary with per-group trade detail.
   */
  runDirectedTrade: async (symbol, action, mode, buyCashPolicy, onProgress) => {
    const normalize = (data) => {
      if (typeof data === 'string') return safeJsonParse(data, {});
      if (data) return sanitizeData(data);
      return data;
    };

    try {
      const startResp = await rrApiClient.post('/rebalancer-reports/directed-trade/run', {
        symbol,
        action,
        mode,
        buy_cash_policy: buyCashPolicy,
      });
      const jobId = startResp.data?.job_id;
      if (!jobId) {
        // Fallback: server returned a full (synchronous) result.
        return normalize(startResp.data);
      }

      // Poll for status/partial results until terminal.
      const pollIntervalMs = 2000;
      const maxAttempts = 450; // ~15 minutes safety cap
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        let snapshot;
        try {
          // eslint-disable-next-line no-await-in-loop
          const statusResp = await rrApiClient.get(
            `/rebalancer-reports/directed-trade/run/status/${jobId}`
          );
          snapshot = normalize(statusResp.data);
        } catch (pollError) {
          // Transient poll failure — keep trying.
          console.warn('Directed trade poll failed, retrying:', pollError?.message);
          // eslint-disable-next-line no-continue
          continue;
        }

        if (onProgress && snapshot) onProgress(snapshot);

        const status = snapshot?.status;
        if (status === 'completed') return snapshot;
        if (status === 'failed') {
          throw new Error(snapshot?.error || 'Directed trade run failed');
        }
      }
      throw new Error('Directed trade run timed out while polling for results.');
    } catch (error) {
      console.error('Error running directed trade:', error);
      throw error;
    }
  },

  /**
   * Queue directed trades for a single group into the dedicated
   * Directed Trade queue.
   * @param {Object} payload - { group_name, symbol, action, mode, trades, account_allocations }
   * @returns {Promise<Object>} Queue result.
   */
  queueDirectedTrade: async (payload) => {
    try {
      const response = await rrApiClient.post('/rebalancer-reports/directed-trade/queue', payload);
      return response.data;
    } catch (error) {
      console.error('Error queuing directed trade:', error);
      throw error;
    }
  },

  /**
   * List all queued directed trades for a date.
   * @param {string} [date] - Optional YYYY-MM-DD. Defaults to today.
   * @returns {Promise<{date: string, count: number, total_trades: number, queued: Object[]}>}
   */
  getDirectedTradeQueue: async (date = null) => {
    try {
      const params = date ? { date } : {};
      const response = await rrApiClient.get('/rebalancer-reports/directed-trade/queue', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching directed trade queue:', error);
      throw error;
    }
  },

  /**
   * Delete a single queued directed trade entry.
   * @param {string} symbol - Symbol of the queued directed trade.
   * @param {string} groupName - Group/account family name.
   * @param {string} [date] - Optional YYYY-MM-DD. Defaults to today.
   * @returns {Promise<Object>} Deletion result.
   */
  deleteDirectedTradeQueueItem: async (symbol, groupName, date = null) => {
    try {
      const params = { symbol, group_name: groupName };
      if (date) params.date = date;
      const response = await rrApiClient.delete('/rebalancer-reports/directed-trade/queue', { params });
      return response.data;
    } catch (error) {
      console.error('Error deleting directed trade queue item:', error);
      throw error;
    }
  },

  /**
   * Download bulk order files for all queued directed trades as a ZIP.
   * @param {string} [date] - Optional YYYY-MM-DD. Defaults to today.
   * @returns {Promise<{blob: Blob, filename: string, contentType: string}>}
   */
  downloadDirectedTradeOrders: async (date = null) => {
    try {
      const params = date ? { date } : {};
      const response = await rrApiClient.post('/rebalancer-reports/directed-trade/download', null, {
        params,
        responseType: 'blob',
      });

      const contentType = response.headers['content-type'] || 'application/zip';
      const contentDisposition = response.headers['content-disposition'];
      let filename = null;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        const today = new Date().toISOString().slice(0, 10);
        filename = `Directed_Trade_Orders_${today}.zip`;
      }

      return {
        blob: response.data,
        filename,
        contentType,
      };
    } catch (error) {
      console.error('Error downloading directed trade orders:', error);
      throw error;
    }
  }
};

export default rrService;
